import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import DataTable, {TableColumn} from 'react-data-table-component'
import {Modal, Spinner} from 'react-bootstrap'
import {KTIcon} from 'src/marketConnect/helpers'
import {useAppDispatch} from 'src/redux/storeHooks'
import {
  createEmailTemplateAsync,
  deleteEmailTemplateAsync,
  getEmailTemplateByIdAsync,
  getEmailTemplatesAsync,
  updateEmailTemplateAsync,
} from 'src/redux/actions/emailTemplateActions'
import {EmailTemplate} from 'src/interfaces/EmailTemplate.interface'
import useAppToast from 'src/hooks/useAppToast'
import {extractAPIErrorMessage} from 'src/utils/extractAPIErrorMessage'
import Loader from 'src/utils/Loader'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import EmailTemplateForm, {
  EMAIL_TEMPLATE_TYPES,
  EmailTemplateFormValues,
  emptyEmailTemplateForm,
} from './EmailTemplateForm'
import './superadmin-email-templates.scss'

const formatTemplateId = (id: number) => `#T-EML${String(id).padStart(3, '0')}`

const formatTypeLabel = (type: string) => {
  const match = EMAIL_TEMPLATE_TYPES.find((item) => item.value === type)
  return match?.label ?? type.replace(/_/g, ' ')
}

const isSuccessResponse = (payload: any) =>
  payload?.status_code === 200 ||
  payload?.status_code === 201 ||
  payload?.status_code === 204 ||
  (payload?.data && payload?.status_code < 400)

const hasEmailBodyContent = (html: string) => {
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
  return text.length > 0
}

const parseTemplateListPayload = (payload: any): {rows: EmailTemplate[]; total: number} => {
  if (!payload || payload?.status_code >= 400) {
    return {rows: [], total: 0}
  }

  const data = payload?.data ?? payload
  const results = data?.results ?? data?.items ?? (Array.isArray(data) ? data : [])

  return {
    rows: results,
    total: data?.count ?? results.length ?? 0,
  }
}

const templateToFormValues = (item: EmailTemplate): EmailTemplateFormValues => ({
  type: item.type || '',
  name: item.name || '',
  template_data: item.template_data || '',
  is_active: Boolean(item.is_active),
})

const ManageEmailTemplates: FC = () => {
  const dispatch = useAppDispatch()
  const {successToast, errorToast} = useAppToast()
  const errorToastRef = useRef(errorToast)

  useEffect(() => {
    errorToastRef.current = errorToast
  }, [errorToast])

  const [rows, setRows] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalRows, setTotalRows] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [showFormModal, setShowFormModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formValues, setFormValues] = useState<EmailTemplateFormValues>(emptyEmailTemplateForm)
  const [submitting, setSubmitting] = useState(false)
  const [loadingForm, setLoadingForm] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [previewItem, setPreviewItem] = useState<EmailTemplate | null>(null)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setCurrentPage(1)
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [search])

  const loadTemplates = useCallback(async () => {
    setLoading(true)

    try {
      const response = await dispatch(
        getEmailTemplatesAsync({
          page: currentPage,
          limit: perPage,
          search: debouncedSearch || undefined,
        })
      )

      if (getEmailTemplatesAsync.rejected.match(response)) {
        errorToastRef.current('Failed to load email templates.')
        setRows([])
        setTotalRows(0)
        return
      }

      const parsed = parseTemplateListPayload(response.payload)
      setRows(parsed.rows)
      setTotalRows(parsed.total)
    } catch {
      errorToastRef.current('Failed to load email templates.')
      setRows([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [currentPage, debouncedSearch, dispatch, perPage])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const handleOpenCreate = () => {
    setEditingId(null)
    setFormValues(emptyEmailTemplateForm())
    setShowFormModal(true)
  }

  const handleCloseFormModal = () => {
    if (submitting || loadingForm) return
    setShowFormModal(false)
    setEditingId(null)
  }

  const handleOpenEdit = useCallback(async (row: EmailTemplate) => {
    setEditingId(row.id)
    setFormValues(templateToFormValues(row))
    setShowFormModal(true)
    setLoadingForm(true)

    try {
      const response = await dispatch(getEmailTemplateByIdAsync(row.id))
      if (getEmailTemplateByIdAsync.rejected.match(response)) {
        errorToastRef.current('Failed to load template details.')
        return
      }

      const data = response.payload?.data ?? response.payload
      if (data?.id) {
        setFormValues(templateToFormValues(data))
      }
    } catch {
      errorToastRef.current('Failed to load template details.')
    } finally {
      setLoadingForm(false)
    }
  }, [dispatch])

  const handleFormChange = (field: keyof EmailTemplateFormValues, value: string | boolean) => {
    setFormValues((prev) => ({...prev, [field]: value}))
  }

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!formValues.type.trim()) {
      errorToast('Template name is required.')
      return
    }

    if (!formValues.name.trim()) {
      errorToast('Email subject line is required.')
      return
    }

    if (!hasEmailBodyContent(formValues.template_data)) {
      errorToast('Email body content is required.')
      return
    }

    setSubmitting(true)

    const payload = {
      type: formValues.type.trim(),
      name: formValues.name.trim(),
      template_data: formValues.template_data,
      is_active: formValues.is_active,
    }

    const fallback = `Failed to ${editingId ? 'update' : 'create'} email template.`
    const saveAction = editingId ? updateEmailTemplateAsync : createEmailTemplateAsync

    try {
      const response = editingId
        ? await dispatch(updateEmailTemplateAsync({id: editingId, payload}))
        : await dispatch(createEmailTemplateAsync(payload))

      if (saveAction.fulfilled.match(response) && isSuccessResponse(response.payload)) {
        successToast(editingId ? 'Email template updated successfully.' : 'Email template created successfully.')
        setShowFormModal(false)
        setEditingId(null)
        setFormValues(emptyEmailTemplateForm())
        loadTemplates()
        return
      }

      errorToast(extractAPIErrorMessage(response.payload, fallback) ?? fallback)
    } catch {
      errorToast(fallback)
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget) return
    setDeleting(true)

    dispatch(deleteEmailTemplateAsync(deleteTarget.id))
      .then((response) => {
        if (isSuccessResponse(response.payload) || deleteEmailTemplateAsync.fulfilled.match(response)) {
          successToast('Email template deleted successfully.')
          setDeleteTarget(null)
          if (previewItem?.id === deleteTarget.id) {
            setPreviewItem(null)
          }
          loadTemplates()
          return
        }
        errorToast(extractAPIErrorMessage(response.payload, 'Failed to delete email template.') ?? 'Failed to delete email template.')
      })
      .catch((error) =>
        errorToast(extractAPIErrorMessage(error, 'Failed to delete email template.') ?? 'Failed to delete email template.')
      )
      .finally(() => setDeleting(false))
  }

  const columns: TableColumn<EmailTemplate>[] = useMemo(
    () => [
      {
        name: 'Template ID',
        width: '130px',
        cell: (row) => (
          <button
            type='button'
            className='superadmin-email-template-id-link'
            onClick={() => setPreviewItem(row)}
          >
            {formatTemplateId(row.id)}
          </button>
        ),
      },
      {
        name: 'Template Name',
        minWidth: '220px',
        cell: (row) => <span className='fw-semibold'>{formatTypeLabel(row.type)}</span>,
      },
      {
        name: 'Email Subject Line',
        minWidth: '320px',
        cell: (row) => (
          <div className='superadmin-email-template-subject-cell' title={row.name}>
            {row.name}
          </div>
        ),
      },
      {
        name: 'Status',
        width: '120px',
        cell: (row) => (
          <span
            className={`superadmin-email-template-status ${
              row.is_active ? 'is-active' : 'is-inactive'
            }`}
          >
            {row.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        name: 'Action',
        width: '110px',
        center: true,
        cell: (row) => (
          <div className='d-flex justify-content-center gap-1'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-primary'
              title='Edit template'
              onClick={() => handleOpenEdit(row)}
            >
              <KTIcon iconName='pencil' className='fs-3' />
            </button>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-danger'
              title='Delete template'
              onClick={() => setDeleteTarget(row)}
            >
              <KTIcon iconName='trash' className='fs-3' />
            </button>
          </div>
        ),
      },
    ],
    [handleOpenEdit]
  )

  return (
    <div className='superadmin-email-templates-page'>
      <div className='d-flex align-items-center justify-content-between mb-5'>
        <h1 className='fs-2 fw-bold mb-0'>Bulk Email Templates</h1>
      </div>

      <div className='card superadmin-email-templates-table-card mb-0'>
        <div className='superadmin-email-templates-controls'>
          <div className='superadmin-email-templates-filter-search'>
            <input
              type='text'
              className='form-control form-control-solid'
              placeholder='Search template names or subjects...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className='superadmin-email-templates-filter-spacer' />

          <button type='button' className='btn btn-sm btn-primary fw-bold text-nowrap' onClick={handleOpenCreate}>
            <KTIcon iconName='plus' className='fs-4 me-1' />
            Add New
          </button>
        </div>

        <TableLoadingShell loading={loading}>
        <DataTable
          key={`${perPage}-${debouncedSearch}`}
          columns={columns}
          data={rows}
          progressPending={false}
          noDataComponent={
            <div className='py-10 text-center text-muted fw-semibold'>No email templates found.</div>
          }
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
          paginationDefaultPage={currentPage}
          paginationResetDefaultPage
          onChangePage={setCurrentPage}
          onChangeRowsPerPage={(newPerPage) => {
            setPerPage(newPerPage)
            setCurrentPage(1)
          }}
          responsive
          customStyles={{
            headCells: {
              style: {fontWeight: 700, fontSize: '14px', backgroundColor: '#fafafa'},
            },
            cells: {
              style: {fontSize: '14px'},
            },
          }}
        />
        </TableLoadingShell>
      </div>

      <Modal show={showFormModal} onHide={handleCloseFormModal} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>
            {editingId ? 'Edit Bulk Email Template' : 'Create Bulk Email Template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingForm ? (
            <div className='py-10 d-flex justify-content-center'>
              <Loader variant="section" />
            </div>
          ) : (
            <EmailTemplateForm
              formId='email-template-form-modal'
              values={formValues}
              onChange={handleFormChange}
              onSubmit={handleSave}
              submitting={submitting}
              isEdit={Boolean(editingId)}
            />
          )}
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <button
            type='button'
            className='btn btn-sm btn-light fw-semibold'
            onClick={handleCloseFormModal}
            disabled={submitting || loadingForm}
          >
            Cancel
          </button>
          <button
            type='submit'
            form='email-template-form-modal'
            className='btn btn-sm btn-primary fw-bold'
            disabled={submitting || loadingForm}
          >
            {submitting ? 'Saving...' : 'Save Template'}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(previewItem)} onHide={() => setPreviewItem(null)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>
            {previewItem ? formatTypeLabel(previewItem.type) : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewItem ? (
            <>
              <div className='d-flex flex-wrap gap-4 mb-4 text-muted fs-7'>
                <span>{formatTemplateId(previewItem.id)}</span>
                <span
                  className={`superadmin-email-template-status ${
                    previewItem.is_active ? 'is-active' : 'is-inactive'
                  }`}
                >
                  {previewItem.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className='fw-semibold mb-2'>Subject</p>
              <p className='text-gray-700 mb-5'>{previewItem.name}</p>
              <p className='fw-semibold mb-2'>Body</p>
              <iframe
                className='email-template-preview-frame'
                title={`${previewItem.name} email preview`}
                srcDoc={previewItem.template_data}
                sandbox=''
              />
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <button type='button' className='btn btn-sm btn-light fw-semibold' onClick={() => setPreviewItem(null)}>
            Close
          </button>
          {previewItem ? (
            <button
              type='button'
              className='btn btn-sm btn-primary fw-bold'
              onClick={() => {
                const item = previewItem
                setPreviewItem(null)
                handleOpenEdit(item)
              }}
            >
              Edit Template
            </button>
          ) : null}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>Delete Email Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-0'>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget ? formatTemplateId(deleteTarget.id) : ''}</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <button
            type='button'
            className='btn btn-sm btn-light fw-semibold'
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button type='button' className='btn btn-sm btn-danger fw-bold' onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ManageEmailTemplates
